import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Users, Calendar as CalIcon, PoundSterling, MessageSquare, Plus, Trash2, Pencil, Download, Upload, Newspaper, Send, HelpCircle, Clock, X, AlarmClockCheck } from "lucide-react";
import ElectionsManager from "./admin/ElectionsManager";
import GovernanceSendManager from "./admin/GovernanceSendManager";
import TrusteeDigestManager from "./admin/TrusteeDigestManager";
import OpsConsole from "./admin/OpsConsole";
import CioChecklist from "./admin/CioChecklist";
import MeetingMinutesManager from "./admin/MeetingMinutesManager";
import CmsManager from "./admin/CmsManager";
import TrusteeRolesManager from "./admin/TrusteeRolesManager";
import PostApprovalManager from "./admin/PostApprovalManager";
import KarunaFundManager from "./admin/KarunaFundManager";
import TrusteeDeclarationManager from "./admin/TrusteeDeclarationManager";
import MeetingsManager from "./admin/MeetingsManager";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

async function downloadCsv(path, suggestedName) {
  try {
    const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Download started");
  } catch (err) {
    toast.error("Download failed");
  }
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [txns, setTxns] = useState([]);
  const [messages, setMessages] = useState([]);
  const [volunteers, setVolunteers] = useState([]);

  const loadAll = async () => {
    try {
      const [s, m, e, t, msgs, vols] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/members"),
        api.get("/events?upcoming_only=false"),
        api.get("/admin/transactions"),
        api.get("/admin/messages"),
        api.get("/admin/volunteers"),
      ]);
      setStats(s.data);
      setMembers(m.data.members);
      setEvents(e.data);
      setTxns(t.data.transactions);
      setMessages(msgs.data.messages);
      setVolunteers(vols.data.applications);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { loadAll(); }, []);

  const statCards = stats
    ? [
        { icon: Users, label: "Members", value: stats.total_members, sub: `${stats.active_members} active`, color: "#001B3A" },
        { icon: CalIcon, label: "Events", value: stats.upcoming_events, sub: `${stats.total_events} total`, color: "#CFA052" },
        { icon: PoundSterling, label: "Donations", value: `£${stats.total_donations_gbp}`, sub: `£${stats.total_membership_gbp} membership`, color: "#E07A5F" },
        { icon: MessageSquare, label: "Messages", value: stats.contact_messages, sub: "From contact form", color: "#0A2540" },
      ]
    : [];

  return (
    <section className="py-16 bg-pearl min-h-[calc(100vh-5rem)]" data-testid="admin-page">
      <div className="section-container">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="eyebrow">Control room</p>
            <h1 className="mt-3 font-display text-5xl text-navy">Admin Dashboard</h1>
          </div>
          <Link
            to="/admin/help"
            data-testid="admin-help-link"
            className="inline-flex items-center gap-2 px-4 py-2 border border-navy/20 text-navy text-sm hover:bg-navy hover:text-pearl transition-colors"
          >
            <HelpCircle size={16} /> How to manage the site
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white border border-navy/10 p-6 rounded-sm">
                <div className="flex items-center justify-between">
                  <div className="eyebrow">{s.label}</div>
                  <Icon size={18} style={{ color: s.color }} />
                </div>
                <div className="mt-3 font-display text-4xl text-navy">{s.value ?? "—"}</div>
                <div className="mt-1 text-xs text-slate-500">{s.sub}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 border-b border-navy/10 flex gap-6 overflow-x-auto">
          {["members", "events", "newsletter", "donations", "messages", "volunteers", "badges", "grants", "elections", "governance", "digest", "cio", "minutes", "cms", "trustees", "declaration", "meetings", "ops", "post-approval", "karuna", "settings"].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`pb-3 text-sm uppercase tracking-widest transition-colors whitespace-nowrap ${tab === k ? "text-navy border-b-2 border-gold" : "text-slate-500 hover:text-navy"}`}
            >
              {k}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "members" && <MembersTable rows={members} onChange={loadAll} />}
          {tab === "events" && <EventsManager rows={events} onChange={loadAll} />}
          {tab === "newsletter" && <NewsletterManager />}
          {tab === "donations" && <DonationsTable rows={txns} />}
          {tab === "messages" && <MessagesList rows={messages} />}
          {tab === "volunteers" && <VolunteersTable rows={volunteers} onChange={loadAll} />}
          {tab === "badges" && <BadgesManager />}
          {tab === "grants" && <GrantsManager />}
          {tab === "elections" && <ElectionsManager />}
          {tab === "governance" && <GovernanceSendManager />}
          {tab === "digest" && <TrusteeDigestManager />}
          {tab === "cio" && <CioChecklist />}
          {tab === "minutes" && <MeetingMinutesManager />}
          {tab === "cms" && <CmsManager />}
          {tab === "trustees" && <TrusteeRolesManager />}
          {tab === "declaration" && <TrusteeDeclarationManager />}
          {tab === "meetings" && <MeetingsManager />}
          {tab === "ops" && <OpsConsole />}
          {tab === "post-approval" && <PostApprovalManager />}
          {tab === "karuna" && <KarunaFundManager />}
          {tab === "settings" && <SettingsPanel />}
        </div>
      </div>
    </section>
  );
}

function MembersTable({ rows, onChange }) {
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/api/admin/import/members`, {
        method: "POST", credentials: "include", body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Import failed");
      toast.success(`${json.created} added · ${json.skipped} skipped · ${json.errors} errors`);
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          onChange={handleImport}
          className="hidden"
          data-testid="admin-members-import-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          data-testid="admin-members-import-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-navy/20 text-navy rounded-sm hover:bg-navy/5 disabled:opacity-50"
        >
          <Upload size={14} /> {importing ? "Importing…" : "Import CSV"}
        </button>
        <button
          onClick={() => downloadCsv("/api/admin/export/members.csv", "abs-uk-members.csv")}
          data-testid="admin-members-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-pearl rounded-sm hover:bg-navy-light"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto bg-white border border-navy/10 rounded-sm">
        <table className="min-w-full text-sm" data-testid="admin-members-table">
          <thead className="bg-navy/5 text-navy/70 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">ID</th>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Membership</th>
              <th className="text-left p-4">Joined</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.user_id} className="border-t border-navy/5">
                <td className="p-4 font-mono text-xs text-slate-600">{m.membership_id || "—"}</td>
                <td className="p-4 text-navy">{m.name}</td>
                <td className="p-4 text-slate-700">{m.email}</td>
                <td className="p-4"><span className="text-xs px-2 py-1 bg-navy/5 rounded-sm">{m.role}</span></td>
                <td className="p-4"><span className={`text-xs px-2 py-1 rounded-sm ${m.membership_status === "active" ? "bg-saffron/15 text-saffron" : "bg-navy/10 text-navy/60"}`}>{m.membership_status}</span></td>
                <td className="p-4 text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString("en-GB") : "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="p-6 text-slate-500 text-center">No members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EventsManager({ rows, onChange }) {
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = { title: "", description: "", event_date: "", location: "", mode: "in_person", capacity: "", image_url: "" };
  const [f, setF] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const startEdit = (ev) => {
    // datetime-local needs YYYY-MM-DDTHH:mm in local time
    const d = new Date(ev.event_date);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setF({
      title: ev.title || "",
      description: ev.description || "",
      event_date: local,
      location: ev.location || "",
      mode: ev.mode || "in_person",
      capacity: ev.capacity ? String(ev.capacity) : "",
      image_url: ev.image_url || "",
    });
    setEditingId(ev.event_id);
    setShow(true);
  };

  const cancel = () => {
    setShow(false);
    setEditingId(null);
    setF(emptyForm);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        event_date: new Date(f.event_date).toISOString(),
        capacity: f.capacity ? Number(f.capacity) : null,
      };
      if (editingId) {
        await api.put(`/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event created");
      }
      cancel();
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Event deleted");
    onChange();
  };

  return (
    <div>
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <button
          onClick={() => downloadCsv("/api/admin/export/events.csv", "abs-uk-events.csv")}
          data-testid="admin-events-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-navy/20 text-navy rounded-sm hover:bg-navy/5"
        >
          <Download size={14} /> Export CSV
        </button>
        <button onClick={() => { cancel(); setShow(true); }} data-testid="admin-new-event-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-pearl rounded-sm hover:bg-navy-light">
          <Plus size={16} /> New event
        </button>
      </div>
      {show && (
        <form onSubmit={submit} className="bg-white border border-navy/10 p-6 rounded-sm mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-event-form">
          <div className="sm:col-span-2 text-xs uppercase tracking-widest text-saffron font-semibold">
            {editingId ? "Editing event" : "New event"}
          </div>
          <input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required className="border border-navy/15 px-3 py-2 rounded-sm sm:col-span-2" data-testid="ae-title" />
          <textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required className="border border-navy/15 px-3 py-2 rounded-sm sm:col-span-2" rows="3" data-testid="ae-desc" />
          <input type="datetime-local" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} required className="border border-navy/15 px-3 py-2 rounded-sm" data-testid="ae-date" />
          <input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} required className="border border-navy/15 px-3 py-2 rounded-sm" data-testid="ae-location" />
          <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm" data-testid="ae-mode">
            <option value="in_person">In Person</option>
            <option value="virtual">Virtual</option>
            <option value="hybrid">Hybrid</option>
          </select>
          <input type="number" placeholder="Capacity" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm" data-testid="ae-capacity" />
          <input placeholder="Image URL (optional)" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm sm:col-span-2" data-testid="ae-image" />
          <div className="sm:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={cancel} className="px-4 py-2 text-navy/70">Cancel</button>
            <button type="submit" disabled={submitting} className="px-6 py-2 bg-gold text-navy rounded-sm hover:bg-gold-light" data-testid="ae-submit">
              {submitting ? "Saving…" : editingId ? "Save changes" : "Create event"}
            </button>
          </div>
        </form>
      )}
      <div className="overflow-x-auto bg-white border border-navy/10 rounded-sm">
        <table className="min-w-full text-sm" data-testid="admin-events-table">
          <thead className="bg-navy/5 text-navy/70 text-xs uppercase tracking-widest">
            <tr><th className="text-left p-4">Title</th><th className="text-left p-4">Date</th><th className="text-left p-4">Mode</th><th className="text-left p-4">RSVPs</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((ev) => (
              <tr key={ev.event_id} className="border-t border-navy/5">
                <td className="p-4 text-navy">{ev.title}</td>
                <td className="p-4 text-slate-700">{new Date(ev.event_date).toLocaleString("en-GB")}</td>
                <td className="p-4"><span className="text-xs px-2 py-1 bg-navy/5 rounded-sm">{ev.mode}</span></td>
                <td className="p-4 text-slate-700">{ev.rsvp_count || 0}{ev.capacity ? ` / ${ev.capacity}` : ""}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button onClick={() => startEdit(ev)} className="text-navy/70 hover:text-navy" data-testid={`edit-event-${ev.event_id}`} aria-label="Edit event">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => remove(ev.event_id)} className="text-saffron hover:text-saffron-light" data-testid={`delete-event-${ev.event_id}`} aria-label="Delete event">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="5" className="p-6 text-slate-500 text-center">No events yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DonationsTable({ rows }) {
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => downloadCsv("/api/admin/export/donations.csv", "abs-uk-donations.csv")}
          data-testid="admin-donations-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-pearl rounded-sm hover:bg-navy-light"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto bg-white border border-navy/10 rounded-sm">
        <table className="min-w-full text-sm" data-testid="admin-donations-table">
        <thead className="bg-navy/5 text-navy/70 text-xs uppercase tracking-widest">
          <tr><th className="text-left p-4">Date</th><th className="text-left p-4">Donor</th><th className="text-left p-4">Type</th><th className="text-left p-4">Amount</th><th className="text-left p-4">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.session_id} className="border-t border-navy/5">
              <td className="p-4 text-slate-700">{t.created_at ? new Date(t.created_at).toLocaleDateString("en-GB") : "—"}</td>
              <td className="p-4 text-navy">{t.donor_name || "Anonymous"}</td>
              <td className="p-4"><span className="text-xs px-2 py-1 bg-navy/5 rounded-sm">{t.package_type}</span></td>
              <td className="p-4 text-navy">£{Number(t.amount).toFixed(2)}</td>
              <td className="p-4"><span className={`text-xs px-2 py-1 rounded-sm ${t.payment_status === "paid" ? "bg-saffron/15 text-saffron" : "bg-navy/10 text-navy/60"}`}>{t.payment_status}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="p-6 text-slate-500 text-center">No transactions yet.</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function MessagesList({ rows }) {
  return (
    <div data-testid="admin-messages-list">
      <div className="flex justify-end mb-4">
        <button
          onClick={() => downloadCsv("/api/admin/export/messages.csv", "abs-uk-messages.csv")}
          data-testid="admin-messages-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-pearl rounded-sm hover:bg-navy-light"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="space-y-4">
        {rows.map((m) => (
          <div key={m.message_id} className="bg-white border border-navy/10 p-6 rounded-sm">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-display text-lg text-navy">{m.subject}</div>
                <div className="text-xs text-slate-500 mt-1">{m.name} · {m.email}</div>
              </div>
              <div className="text-xs text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString("en-GB") : ""}</div>
            </div>
            <p className="mt-3 text-slate-700 leading-relaxed">{m.message}</p>
          </div>
        ))}
        {rows.length === 0 && <p className="text-slate-500 text-center py-10">No messages yet.</p>}
      </div>
    </div>
  );
}


function NewsletterManager() {
  const [issues, setIssues] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | <issue>
  const [busy, setBusy] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null); // <issue> when modal open
  const [schedules, setSchedules] = useState([]);

  const load = async () => {
    try {
      const res = await api.get("/admin/newsletter/issues");
      setIssues(res.data.issues || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadSchedules = async () => {
    try {
      const res = await api.get("/admin/newsletter/schedules");
      setSchedules(res.data.schedules || []);
    } catch (err) {
      // 401 if not admin — silently ignore for non-admin uses
    }
  };

  useEffect(() => {
    load();
    loadSchedules();
  }, []);

  const send = async (issue) => {
    if (!window.confirm(`Email this edition to all members?`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/newsletter/issues/${issue.issue_id}/send`);
      const r = res.data;
      toast.success(`${r.delivered_count}/${r.recipient_count} delivered${r.mocked ? " (Resend mocked, logs only)" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cancelSchedule = async (sched) => {
    if (!window.confirm(`Cancel scheduled send for "${sched.issue_title}"?`)) return;
    try {
      await api.delete(`/admin/newsletter/schedules/${sched.schedule_id}`);
      toast.success("Schedule cancelled");
      loadSchedules();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (issue) => {
    if (!window.confirm(`Delete "${issue.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/newsletter/issues/${issue.issue_id}`);
      toast.success("Edition deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (editing) {
    return (
      <NewsletterEditor
        issue={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-newsletter-manager">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-navy/70 max-w-2xl">
          Compose, edit and publish broadsheet-style newsletter editions. Published editions appear at <span className="font-mono text-xs">/newsletter</span> in all 11 languages.
        </p>
        <button
          onClick={() => setEditing("new")}
          data-testid="admin-newsletter-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-pearl rounded-sm hover:bg-navy-light"
        >
          <Plus size={14} /> New edition
        </button>
      </div>
      <div className="overflow-x-auto bg-white border border-navy/10 rounded-sm">
        <table className="min-w-full text-sm" data-testid="admin-newsletter-table">
          <thead className="bg-navy/5 text-navy/70 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">Edition</th>
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Articles</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((i) => (
              <tr key={i.issue_id} className="border-t border-navy/5">
                <td className="p-4 text-navy/80 font-mono text-xs">{i.edition_label}</td>
                <td className="p-4 text-navy">{i.title}</td>
                <td className="p-4 text-slate-600">{i.issue_date}</td>
                <td className="p-4 text-slate-600">{(i.articles || []).length}</td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded-sm ${i.status === "published" ? "bg-saffron/15 text-saffron" : "bg-navy/10 text-navy/60"}`}>
                    {i.status}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditing(i)}
                      data-testid={`admin-newsletter-edit-${i.issue_id}`}
                      className="p-2 text-navy/70 hover:text-navy"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => send(i)}
                      disabled={busy || i.status !== "published"}
                      data-testid={`admin-newsletter-send-${i.issue_id}`}
                      className="p-2 text-saffron hover:text-saffron-light disabled:opacity-30"
                      title={i.status === "published" ? "Send to all members" : "Publish before sending"}
                    >
                      <Send size={14} />
                    </button>
                    <button
                      onClick={() => setScheduleFor(i)}
                      disabled={i.status !== "published"}
                      data-testid={`admin-newsletter-schedule-${i.issue_id}`}
                      className="p-2 text-navy/70 hover:text-saffron disabled:opacity-30"
                      title={i.status === "published" ? "Schedule send" : "Publish before scheduling"}
                    >
                      <Clock size={14} />
                    </button>
                    <button
                      onClick={() => remove(i)}
                      data-testid={`admin-newsletter-delete-${i.issue_id}`}
                      className="p-2 text-red-700 hover:text-red-900"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {issues.length === 0 && (
              <tr><td colSpan="6" className="p-10 text-center text-slate-500">
                <Newspaper size={28} className="mx-auto mb-3 text-saffron" />
                No editions yet, click <span className="font-medium">New edition</span> to compose your first issue.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Schedules panel */}
      <SchedulesPanel schedules={schedules} onCancel={cancelSchedule} />

      {/* Schedule modal */}
      {scheduleFor && (
        <ScheduleSendModal
          issue={scheduleFor}
          onClose={() => setScheduleFor(null)}
          onScheduled={() => {
            setScheduleFor(null);
            loadSchedules();
          }}
        />
      )}
    </div>
  );
}

// ── Schedule send: list of scheduled / sent / cancelled rows ──
function SchedulesPanel({ schedules, onCancel }) {
  if (!schedules || schedules.length === 0) return null;
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  const statusBadge = (s) => {
    const map = {
      pending:   { label: "Pending",   cls: "bg-amber-100 text-amber-800 border-amber-200" },
      running:   { label: "Sending",   cls: "bg-blue-100 text-blue-800 border-blue-200" },
      sent:      { label: "Sent",      cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
      cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-700 border-slate-200" },
      failed:    { label: "Failed",    cls: "bg-red-100 text-red-800 border-red-200" },
    };
    const v = map[s] || map.pending;
    return <span className={`text-[10px] px-2 py-0.5 rounded border ${v.cls}`}>{v.label}</span>;
  };

  return (
    <div className="mt-8" data-testid="admin-newsletter-schedules-panel">
      <h3 className="font-display text-lg text-navy inline-flex items-center gap-2">
        <AlarmClockCheck size={16} className="text-saffron" /> Scheduled sends
      </h3>
      <p className="text-xs text-navy/55 mt-1 mb-3">
        Newsletters set to dispatch in the future. The scheduler polls once a minute.
      </p>
      <div className="overflow-x-auto bg-white border border-navy/10 rounded-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-navy/5 text-navy/70 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Edition</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Delivered</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.schedule_id} className="border-t border-navy/5" data-testid={`schedule-row-${s.schedule_id}`}>
                <td className="p-3 text-navy/85 whitespace-nowrap">{fmt(s.scheduled_at)}</td>
                <td className="p-3 text-navy">{s.issue_title || s.issue_id}</td>
                <td className="p-3">{statusBadge(s.status)}</td>
                <td className="p-3 text-slate-600 whitespace-nowrap">
                  {s.status === "sent"
                    ? `${s.delivered_count}/${s.recipient_count}`
                    : s.status === "failed"
                      ? <span className="text-red-700 text-xs">{s.error || "Failed"}</span>
                      : "—"}
                </td>
                <td className="p-3 text-right">
                  {s.status === "pending" && (
                    <button
                      onClick={() => onCancel(s)}
                      data-testid={`schedule-cancel-${s.schedule_id}`}
                      className="text-xs text-red-700 hover:text-red-900 font-medium"
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Modal: pick a date/time to schedule ──
function ScheduleSendModal({ issue, onClose, onScheduled }) {
  // Default to next day 8am local time
  const defaultLocal = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(8, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [when, setWhen] = useState(defaultLocal);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!when) return;
    setBusy(true);
    try {
      const iso = new Date(when).toISOString();
      await api.post(`/admin/newsletter/issues/${issue.issue_id}/schedule`, { scheduled_at: iso });
      toast.success("Scheduled, the newsletter will dispatch automatically.");
      onScheduled();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      data-testid="schedule-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Schedule send</p>
            <h3 className="font-display text-2xl text-navy mt-1">{issue.title}</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1 text-navy/50 hover:text-navy">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-navy/60 mt-3 leading-relaxed">
          Pick a future date and time. The system polls once a minute and dispatches automatically, even if you're offline.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-navy/55">Send at</label>
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              required
              data-testid="schedule-input"
              className="form-input w-full mt-1"
            />
            <p className="text-[11px] text-navy/50 mt-1">Times are in your local timezone.</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="schedule-cancel-btn"
              className="px-4 py-2 text-sm text-navy/70 hover:text-navy"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="schedule-confirm-btn"
              className="inline-flex items-center gap-2 px-5 py-2 bg-saffron text-navy text-sm font-medium rounded hover:bg-saffron/90 disabled:opacity-60"
            >
              <Clock size={14} /> {busy ? "Scheduling…" : "Schedule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewsletterEditor({ issue, onCancel, onSaved }) {
  const isNew = !issue;
  const [form, setForm] = useState(() => ({
    title: issue?.title || "",
    edition_label: issue?.edition_label || `Vol I · No 1 · ${new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`,
    issue_date: issue?.issue_date || new Date().toISOString().slice(0, 10),
    masthead: issue?.masthead || "The Ambedkarite Times",
    editorial_lead: issue?.editorial_lead || "",
    status: issue?.status || "draft",
    articles: issue?.articles?.length
      ? issue.articles.map((a) => ({ ...a }))
      : [{ kicker: "", headline: "", byline: "", dek: "", body_html: "", image_url: "", video_url: "", audio_url: "" }],
  }));
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updArticle = (i, k, v) =>
    setForm((f) => ({ ...f, articles: f.articles.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)) }));
  const addArticle = () =>
    setForm((f) => ({ ...f, articles: [...f.articles, { kicker: "", headline: "", byline: "", dek: "", body_html: "", image_url: "", video_url: "", audio_url: "" }] }));
  const removeArticle = (i) =>
    setForm((f) => ({ ...f, articles: f.articles.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        edition_label: form.edition_label,
        issue_date: form.issue_date,
        masthead: form.masthead,
        editorial_lead: form.editorial_lead,
        status: form.status,
        articles: form.articles
          .filter((a) => (a.headline || "").trim())
          .map((a) => ({
            headline: a.headline,
            kicker: a.kicker || null,
            byline: a.byline || null,
            dek: a.dek || null,
            body_html: a.body_html || "",
            image_url: a.image_url || null,
            video_url: a.video_url || null,
            audio_url: a.audio_url || null,
          })),
      };
      if (isNew) {
        await api.post("/admin/newsletter/issues", payload);
        toast.success("Edition created");
      } else {
        await api.put(`/admin/newsletter/issues/${issue.issue_id}`, payload);
        toast.success("Edition saved");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-navy/10 p-6 rounded-sm space-y-6" data-testid="admin-newsletter-editor">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-navy">{isNew ? "New edition" : `Edit · ${issue.title}`}</h2>
        <button type="button" onClick={onCancel} className="text-sm text-navy/60 hover:text-navy">Cancel</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Title">
          <input data-testid="newsletter-form-title" required value={form.title} onChange={(e) => upd("title", e.target.value)} className="form-input" />
        </Field>
        <Field label="Edition label">
          <input data-testid="newsletter-form-edition" value={form.edition_label} onChange={(e) => upd("edition_label", e.target.value)} className="form-input" />
        </Field>
        <Field label="Issue date">
          <input type="date" data-testid="newsletter-form-date" value={form.issue_date} onChange={(e) => upd("issue_date", e.target.value)} className="form-input" />
        </Field>
        <Field label="Masthead">
          <input data-testid="newsletter-form-masthead" value={form.masthead} onChange={(e) => upd("masthead", e.target.value)} className="form-input" />
        </Field>
        <Field label="Status" className="md:col-span-2">
          <select data-testid="newsletter-form-status" value={form.status} onChange={(e) => upd("status", e.target.value)} className="form-input">
            <option value="draft">Draft (private)</option>
            <option value="published">Published (visible at /newsletter)</option>
          </select>
        </Field>
        <Field label="Editorial lead (optional standfirst)" className="md:col-span-2">
          <textarea
            data-testid="newsletter-form-lead"
            rows={2}
            value={form.editorial_lead}
            onChange={(e) => upd("editorial_lead", e.target.value)}
            className="form-input"
            placeholder="One sentence framing this edition…"
          />
        </Field>
      </div>

      <div className="border-t border-navy/10 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-navy">Articles ({form.articles.length})</h3>
          <button type="button" onClick={addArticle} data-testid="newsletter-form-add-article" className="inline-flex items-center gap-1 text-sm text-saffron hover:text-saffron-light">
            <Plus size={14} /> Add article
          </button>
        </div>
        <div className="space-y-6">
          {form.articles.map((a, i) => (
            <div key={i} className="border border-navy/10 p-4 rounded-sm bg-pearl/40" data-testid={`newsletter-form-article-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-navy/60">{i === 0 ? "Lead article" : `Article ${i + 1}`}</span>
                {form.articles.length > 1 && (
                  <button type="button" onClick={() => removeArticle(i)} className="text-red-700 hover:text-red-900" title="Remove article">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Kicker (small label above)">
                  <input value={a.kicker || ""} onChange={(e) => updArticle(i, "kicker", e.target.value)} className="form-input" placeholder="EDITORIAL · DHAMMA · NEWS" />
                </Field>
                <Field label="Byline">
                  <input value={a.byline || ""} onChange={(e) => updArticle(i, "byline", e.target.value)} className="form-input" placeholder="By the editorial team" />
                </Field>
                <Field label="Headline" className="md:col-span-2">
                  <input required value={a.headline || ""} onChange={(e) => updArticle(i, "headline", e.target.value)} className="form-input" placeholder="The arresting headline" />
                </Field>
                <Field label="Standfirst / dek (optional)" className="md:col-span-2">
                  <textarea rows={2} value={a.dek || ""} onChange={(e) => updArticle(i, "dek", e.target.value)} className="form-input" />
                </Field>
                <Field label="Image URL (optional)" className="md:col-span-2">
                  <input value={a.image_url || ""} onChange={(e) => updArticle(i, "image_url", e.target.value)} className="form-input" placeholder="https://…" />
                </Field>
                <Field label="Video URL (YouTube or Vimeo, optional)">
                  <input value={a.video_url || ""} onChange={(e) => updArticle(i, "video_url", e.target.value)} className="form-input" placeholder="https://youtube.com/watch?v=… or vimeo.com/12345" data-testid={`nl-art-video-${i}`} />
                </Field>
                <Field label="Audio URL (mp3 / podcast, optional)">
                  <input value={a.audio_url || ""} onChange={(e) => updArticle(i, "audio_url", e.target.value)} className="form-input" placeholder="https://…/talk.mp3" data-testid={`nl-art-audio-${i}`} />
                </Field>
                <Field label="Body, separate paragraphs with a blank line" className="md:col-span-2">
                  <textarea
                    rows={8}
                    value={a.body_html || ""}
                    onChange={(e) => updArticle(i, "body_html", e.target.value)}
                    className="form-input font-serif"
                    placeholder={"First paragraph, will get a drop-cap.\n\nSecond paragraph.\n\nThird paragraph."}
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-navy/10">
        <button type="button" onClick={onCancel} className="px-5 py-2 text-navy/70 hover:text-navy">Cancel</button>
        <button
          type="submit"
          disabled={saving}
          data-testid="newsletter-form-submit"
          className="px-6 py-2.5 bg-navy text-pearl rounded-sm hover:bg-navy-light disabled:opacity-50"
        >
          {saving ? "Saving…" : isNew ? "Create edition" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">{label}</span>
      {children}
    </label>
  );
}


function VolunteersTable({ rows, onChange }) {
  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/volunteers/${id}`, { status });
      toast.success("Updated");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
    }
  };
  const STATUS_COLOR = {
    new: "bg-saffron/15 text-saffron border-saffron/40",
    contacted: "bg-blue-100 text-blue-700 border-blue-300",
    active: "bg-emerald-100 text-emerald-700 border-emerald-300",
    declined: "bg-slate-100 text-slate-500 border-slate-300",
    archived: "bg-slate-100 text-slate-400 border-slate-200",
  };
  return (
    <div className="bg-white border border-navy/10 rounded-sm" data-testid="admin-volunteers-table">
      <div className="px-4 py-3 border-b border-navy/10 flex items-center justify-between">
        <h3 className="font-medium text-navy">Volunteer applications · {rows.length}</h3>
        <span className="text-xs text-slate-500">Newest first</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-center text-slate-500 py-10">No applications yet. Share the <code>/volunteer</code> page in your newsletter.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-pearl">
              <tr>
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Roles</th>
                <th className="px-4 py-3">Commitment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {rows.map((r) => (
                <tr key={r.application_id} className="align-top" data-testid={`vol-row-${r.application_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-navy">{r.name}</div>
                    <a href={`mailto:${r.email}`} className="text-xs text-slate-500 hover:text-navy">{r.email}</a>
                    {r.phone && <div className="text-xs text-slate-500">{r.phone}</div>}
                    {r.location && <div className="text-xs text-slate-400">{r.location}{r.age_range ? ` · ${r.age_range}` : ""}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {(r.role_ids || []).map((id) => (
                        <span key={id} className="px-2 py-0.5 bg-saffron/10 border border-saffron/30 text-saffron text-[10px] rounded-sm uppercase tracking-widest">{id}</span>
                      ))}
                    </div>
                    {r.languages && <div className="text-xs text-slate-500 mt-1">🌍 {r.languages}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[160px]">
                    {r.commitment || "—"}
                    {r.motivation && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-navy/70 hover:text-navy text-[11px]">Motivation</summary>
                        <p className="mt-1 text-[12px] text-slate-700 italic whitespace-pre-wrap">{r.motivation}</p>
                      </details>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest border ${STATUS_COLOR[r.status] || STATUS_COLOR.new}`}>
                      {r.status || "new"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={r.status || "new"}
                      onChange={(e) => updateStatus(r.application_id, e.target.value)}
                      data-testid={`vol-status-${r.application_id}`}
                      className="text-xs border border-navy/15 rounded-sm px-2 py-1 bg-white"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="active">Active</option>
                      <option value="declined">Declined</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function BadgesManager() {
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get("/community/badges/catalog").then((r) => setCatalog(r.data.badges || [])).catch(() => {});
  }, []);

  const search = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) { setUsers([]); return; }
    setSearching(true);
    try {
      const r = await api.get(`/admin/users/search?q=${encodeURIComponent(q.trim())}`);
      setUsers(r.data.users || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const grant = async (userId, badge_type) => {
    setBusy((b) => ({ ...b, [`${userId}:${badge_type}`]: true }));
    try {
      await api.post(`/admin/users/${userId}/badges`, { badge_type });
      toast.success("Badge granted");
      await search();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not grant");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[`${userId}:${badge_type}`]; return c; });
    }
  };

  const revoke = async (userId, badge) => {
    if (!window.confirm(`Remove "${badge.label}" from this member?`)) return;
    try {
      await api.delete(`/admin/users/${userId}/badges/${badge.badge_id}`);
      toast.success("Badge removed");
      await search();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <div className="bg-white border border-navy/10 rounded-sm" data-testid="admin-badges-manager">
      <div className="px-4 py-3 border-b border-navy/10">
        <h3 className="font-medium text-navy">Volunteer & community badges</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Find a member by name, email, or membership ID. Grant or revoke badges.
          Approving a volunteer application automatically grants the Volunteer badge.
        </p>
      </div>
      <form onSubmit={search} className="px-4 py-3 flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name, email, or ABSUK-202… ID"
          className="form-input flex-1"
          data-testid="admin-badges-search"
        />
        <button type="submit" className="px-4 py-2 bg-navy text-pearl text-sm rounded-sm hover:bg-navy-light disabled:opacity-40" disabled={searching}>
          {searching ? "Searching…" : "Search"}
        </button>
      </form>

      {users.length === 0 && q && !searching && (
        <p className="text-center text-slate-500 py-8 text-sm">No matches.</p>
      )}

      <div className="divide-y divide-navy/5">
        {users.map((u) => (
          <div key={u.user_id} className="p-4" data-testid={`admin-badge-row-${u.user_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-medium text-navy">{u.name || "Member"}</div>
                <div className="text-xs text-slate-500">{u.email} · {u.membership_id || "no ID"} · {u.role}</div>
              </div>
              {u.badges?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {u.badges.map((b) => (
                    <span key={b.badge_id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-saffron/10 border border-saffron/30 text-saffron text-[10px] rounded-sm uppercase tracking-wider">
                      {b.label}
                      <button onClick={() => revoke(u.user_id, b)} className="hover:text-red-700 ml-1" title="Revoke">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {catalog.map((b) => {
                const has = (u.badges || []).some((x) => x.type === b.type);
                if (has) return null;
                const key = `${u.user_id}:${b.type}`;
                return (
                  <button
                    key={b.type}
                    onClick={() => grant(u.user_id, b.type)}
                    disabled={busy[key]}
                    className="px-2 py-1 bg-pearl border border-navy/15 hover:border-saffron hover:text-saffron text-[11px] rounded-sm disabled:opacity-40"
                    data-testid={`admin-badge-grant-${u.user_id}-${b.type}`}
                  >
                    + {b.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function GrantsManager() {
  const [apps, setApps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [stats, setStats] = useState(null);
  const [actionFor, setActionFor] = useState(null);

  const load = async () => {
    try {
      const url = statusFilter ? `/admin/grants?status_filter=${statusFilter}` : "/admin/grants";
      const r = await api.get(url);
      setApps(r.data.applications || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load");
    }
    try {
      const s = await api.get("/grants/public-stats");
      setStats(s.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const STATUS_COLOR = {
    new: "bg-saffron/15 text-saffron border-saffron/40",
    shortlisted: "bg-blue-100 text-blue-700 border-blue-300",
    awarded: "bg-emerald-100 text-emerald-700 border-emerald-300",
    disbursed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    declined: "bg-slate-100 text-slate-500 border-slate-300",
  };

  return (
    <div className="bg-white border border-navy/10 rounded-sm" data-testid="admin-grants-manager">
      <div className="px-4 py-3 border-b border-navy/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-medium text-navy">Grant applications · {apps.length}</h3>
          {stats && (
            <p className="text-xs text-slate-500 mt-0.5">
              {stats.applications_received} received · {stats.applications_awarded} awarded · {stats.applications_disbursed} disbursed · £{stats.total_awarded_gbp} total
            </p>
          )}
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="form-input text-xs" data-testid="admin-grants-filter">
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="awarded">Awarded</option>
          <option value="disbursed">Disbursed</option>
          <option value="declined">Declined</option>
        </select>
      </div>
      {apps.length === 0 ? (
        <p className="text-center text-slate-500 py-10">No applications {statusFilter ? `with status "${statusFilter}"` : "yet"}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-pearl">
              <tr>
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Programme</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {apps.map((a) => (
                <tr key={a.application_id} className="align-top" data-testid={`grant-row-${a.application_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-navy">{a.applicant_name}</div>
                    <a href={`mailto:${a.applicant_email}`} className="text-xs text-slate-500 hover:text-navy">{a.applicant_email}</a>
                    {a.organisation && <div className="text-xs text-slate-400">{a.organisation}</div>}
                  </td>
                  <td className="px-4 py-3 max-w-[280px]">
                    <div className="font-medium text-navy">{a.project_title}</div>
                    <div className="text-xs text-slate-500 line-clamp-2">{a.project_summary}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{a.programme_id}</td>
                  <td className="px-4 py-3 font-mono text-navy">£{a.amount_requested}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm ${STATUS_COLOR[a.status] || ""}`}>{a.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString("en-GB")}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setActionFor(a)} className="text-xs px-2 py-1 bg-navy text-pearl rounded-sm hover:bg-navy-light" data-testid={`grant-action-${a.application_id}`}>
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionFor && (
        <GrantActionModal application={actionFor} onClose={() => setActionFor(null)} onUpdated={() => { setActionFor(null); load(); }} />
      )}
    </div>
  );
}

function GrantActionModal({ application, onClose, onUpdated }) {
  const [status, setStatus] = useState(application.status || "new");
  const [note, setNote] = useState(application.admin_note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/grants/${application.application_id}`, { status, note });
      toast.success("Application updated");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-pearl rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-navy/10">
          <div className="font-display text-lg text-navy">{application.project_title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{application.applicant_name} · £{application.amount_requested}</div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/55">Application</div>
            <p className="mt-1 text-sm text-navy/80 whitespace-pre-wrap font-serif">{application.project_summary}</p>
          </div>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input w-full" data-testid="grant-status-select">
              <option value="new">New</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="awarded">Awarded</option>
              <option value="disbursed">Disbursed</option>
              <option value="declined">Declined</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Admin note (internal)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} className="form-input w-full" />
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-navy/10">
          <button onClick={onClose} className="px-4 py-2 text-navy/60 hover:text-navy text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-navy text-pearl rounded-md text-sm disabled:opacity-40" data-testid="grant-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [gate, setGate] = useState(null);
  const [pwInput, setPwInput] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/site-gate");
      setGate(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load");
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (enabled) => {
    if (!window.confirm(
      enabled
        ? "Lock the public site behind the access code? Visitors without the code or a trustee login will see the 'Coming Soon' page."
        : "Open the site to the public? Anyone with the URL will be able to browse without an access code."
    )) return;
    setBusy(true);
    try {
      await api.post("/admin/site-gate", { enabled });
      toast.success(enabled ? "Site is now locked" : "Site is now public");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (!pwInput || pwInput.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!window.confirm("Change the access code? Anyone using the old code will be locked out on their next visit.")) return;
    setBusy(true);
    try {
      await api.post("/admin/site-gate", { new_password: pwInput });
      toast.success("Access code changed");
      setPwInput("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!gate) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="bg-white border border-navy/10 rounded-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <h3 className="font-display text-xl text-navy">Public access</h3>
            <p className="mt-1 text-sm text-slate-600">
              While the charity is in private alpha, the website is locked behind a shared access code.
              Trustees, admins, and anyone with a valid access code can view the site; everyone else sees
              a branded "Coming Soon" page.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Note: you (as an admin) bypass the gate automatically when logged in — that's why you can
              see this page even when the gate is on.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest border ${
                gate.gate_enabled
                  ? "bg-amber-50 text-amber-700 border-amber-300"
                  : "bg-emerald-50 text-emerald-700 border-emerald-300"
              }`}
            >
              {gate.gate_enabled ? "Gate ON · Public access blocked" : "Gate OFF · Site is public"}
            </span>
            <button
              onClick={() => toggle(!gate.gate_enabled)}
              disabled={busy}
              data-testid="admin-gate-toggle"
              className={`px-5 py-2 rounded-md text-sm font-medium disabled:opacity-40 ${
                gate.gate_enabled
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-navy text-pearl hover:bg-navy-light"
              }`}
            >
              {gate.gate_enabled ? "Go live — open to public" : "Lock site again"}
            </button>
          </div>
        </div>
        {gate.updated_at && (
          <p className="mt-3 text-[10px] text-slate-400">
            Last changed: {new Date(gate.updated_at).toLocaleString("en-GB")}
          </p>
        )}
      </div>

      <form onSubmit={changePw} className="bg-white border border-navy/10 rounded-sm p-5" data-testid="admin-gate-password-form">
        <h3 className="font-display text-xl text-navy">Change access code</h3>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Default code: <code className="px-1 py-0.5 bg-pearl border border-navy/10 rounded text-saffron">absuk2026</code>.
          You can rotate the code here at any time. Existing visitors who have already
          unlocked the site will keep access for 30 days unless they clear cookies.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 items-end">
          <label className="flex-1 min-w-[240px]">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">New access code</span>
            <input
              type={showPw ? "text" : "password"}
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              minLength={6}
              maxLength={200}
              placeholder="At least 6 characters"
              className="form-input w-full"
              data-testid="admin-gate-password-input"
            />
          </label>
          <button
            type="button"
            onClick={() => setShowPw((s) => !s)}
            className="px-3 py-2 text-xs bg-pearl border border-navy/15 rounded-md hover:border-saffron"
          >
            {showPw ? "Hide" : "Show"}
          </button>
          <button
            type="submit"
            disabled={busy || pwInput.length < 6}
            className="px-5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40"
            data-testid="admin-gate-password-submit"
          >
            Change code
          </button>
        </div>
      </form>
    </div>
  );
}

